/**
 * Client-side implementation of tag-based CSS extraction.
 * This module extracts CSS rules that apply to HTML tag selectors from the designer canvas
 */

import {
  extractTagCssFromRules,
  type CssRule,
  type MediaRule,
} from '@packages/shared/tag-css-extraction';
import {WEBFLOW_SITE_IFRAME_ID} from '@packages/systems/core/constants/SharedConfig';

const cssSelectorRegex = /^([^{]+)\s*\{/;

function extractStyleSheets(documentElement: Document) {
  const iframe = documentElement.getElementById(
    WEBFLOW_SITE_IFRAME_ID
  ) as HTMLIFrameElement | null;

  if (iframe && iframe.contentDocument && iframe.contentWindow) {
    return iframe.contentDocument.styleSheets;
  }

  // fallback to document for custom code preview panel since it is a seperate origin iframe
  return documentElement.styleSheets;
}

/**
 * Extracts tag-based CSS from the designer canvas.
 * This function directly accesses the stylesheets in the iframe to extract CSS rules.
 * It preserves the original order of CSS rules to maintain correct precedence.
 *
 * @param iframe The iframe containing the designer canvas
 * @returns A string containing CSS for all HTML tags with their styles
 */
export function extractTagCssFromDesignerCanvas(
  documentElement: Document
): string {
  try {
    const styleSheets = extractStyleSheets(documentElement);
    if (!styleSheets) {
      return '';
    }

    // Convert stylesheet rules to the format expected by the shared extractor
    const cssRules: CssRule[] = [];

    // Process each stylesheet
    for (let i = 0; i < styleSheets.length; i++) {
      const styleSheet = styleSheets[i];

      // Skip undefined stylesheets
      if (!styleSheet) continue;

      try {
        // Try to access cssRules - this will throw for cross-origin stylesheets
        const rules = styleSheet.cssRules;
        if (!rules) {
          continue;
        }

        // Process each rule in the stylesheet
        for (let j = 0; j < rules.length; j++) {
          const rule = rules[j];
          if (!rule) continue;

          // Check for style rules using instanceof or type property (fallback for edge cases)
          const isStyleRule =
            rule instanceof CSSStyleRule || rule.type === CSSRule.STYLE_RULE;

          if (isStyleRule) {
            // Handle style rules
            const selectorText = (rule as CSSStyleRule).selectorText || '';

            // Try to extract selector from cssText if selectorText is empty
            let extractedSelector = selectorText;
            if (!selectorText && rule.cssText) {
              const match = rule.cssText.match(cssSelectorRegex);
              if (match && match[1]) {
                extractedSelector = match[1].trim();
              }
            }

            const cssRule = {
              type: 1,
              selectorText: extractedSelector,
              cssText: rule.cssText,
            };
            cssRules.push(cssRule);
          } else if (
            rule instanceof CSSMediaRule ||
            rule.type === CSSRule.MEDIA_RULE
          ) {
            // Handle media rules
            const mediaRule = rule as CSSMediaRule;
            const nestedRules: CssRule[] = [];

            // Process nested rules in the media rule
            if (mediaRule.cssRules) {
              for (let k = 0; k < mediaRule.cssRules.length; k++) {
                const nestedRule = mediaRule.cssRules[k];
                if (!nestedRule) continue;

                const isNestedStyleRule =
                  nestedRule instanceof CSSStyleRule ||
                  nestedRule.type === CSSRule.STYLE_RULE;

                if (isNestedStyleRule) {
                  // Extract selector from nested rule
                  let nestedSelector =
                    (nestedRule as CSSStyleRule).selectorText || '';
                  if (!nestedSelector && nestedRule.cssText) {
                    const match = nestedRule.cssText.match(cssSelectorRegex);
                    if (match && match[1]) {
                      nestedSelector = match[1].trim();
                    }
                  }

                  const nestedCssRule = {
                    type: 1,
                    selectorText: nestedSelector,
                    cssText: nestedRule.cssText,
                  };
                  nestedRules.push(nestedCssRule);
                } else {
                  nestedRules.push({
                    type: 0, // Unknown type
                    selectorText: '',
                    cssText: nestedRule.cssText,
                  });
                }
              }
            }

            // Add the media rule with its nested rules
            cssRules.push({
              type: 4,
              selectorText: '',
              cssText: rule.cssText,
              conditionText:
                mediaRule.conditionText || mediaRule.media.mediaText,
              cssRules: nestedRules,
            } as MediaRule);
          } else {
            // Handle other rule types
            const unknownRule = {
              type: 0, // Unknown type
              selectorText: '',
              cssText: rule.cssText,
            };
            cssRules.push(unknownRule);
          }
        }
      } catch (e) {
        // Skip stylesheets that can't be accessed (e.g., cross-origin)
        // This is expected for stylesheets loaded from different origins
        continue;
      }
    }

    // Use the shared extractor to get tag CSS

    const cssText = extractTagCssFromRules(cssRules);

    if (cssText) {
      return cssText.trim();
    } else {
      return '';
    }
  } catch (e) {
    console.error('[CLIENT] extractTagCssFromDesignerCanvas - Error:', e);
    return '';
  }
}
